import React, {useState, useMemo, useCallback} from "react";
import {
    Button,
    Steps,
    Input,
    Modal,
    message,
    Card,
    Space,
    Typography,
    Divider
} from "antd";
import {
    MailOutlined,
    LeftOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CameraOutlined,
    FileTextOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import s from "../mobile.module.css";
import Report from "./Report";
import Time from "./Time";
import moment from 'moment';
import NewUpload from "./NewUpload";

const {TextArea} = Input;
const {Title, Text} = Typography;
const {Step} = Steps;

const MobileReport = ({object, report, onClose, onSubmit, reportsTemp, plan}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [timeStart, setTimeStart] = useState(
        report ? {
            status: true,
            time: moment(Number(report.time_start) * 1000)
        } : {
            status: false,
            time: null
        }
    );

    const [timeEnd, setTimeEnd] = useState(
        report ? {
            status: true,
            time: moment(Number(report.time_end) * 1000)
        } : {
            status: false,
            time: null
        }
    );

    const [workSpecified, setWorkSpecified] = useState(!!report);
    const [nextReason, setNextReason] = useState({status: false, text: ''});
    const [photoUploaded, setPhotoUploaded] = useState(!!report);

    // Определяем режим: редактирование или создание нового
    const isEditMode = !!report;

    const defaultArr = useMemo(() => {
        if (report) {
            const worksArr = report.works_id?.split(',') || [];
            const commentsArr = report.comments_id?.split('`') || [];

            return reportsTemp.map((el) => {
                const index = worksArr.findIndex(workId => Number(workId) === Number(el.id));
                const isChecked = index !== -1;

                return {
                    ...el,
                    checked: isChecked,
                    inputVal: isChecked ? commentsArr[index] || '' : ''
                };
            });
        }
        return reportsTemp.map(el => ({...el, checked: false, inputVal: ''}));
    }, [report, reportsTemp]);

    const [check, setCheck] = useState(defaultArr);

    const steps = [
        {
            title: 'Время работ',
            icon: <ClockCircleOutlined/>,
            completed: timeStart.status && timeEnd.status
        },
        {
            title: 'Выполненные работы',
            icon: <FileTextOutlined/>,
            completed: workSpecified
        },
        {
            title: 'Фотографии',
            icon: <CameraOutlined/>,
            completed: photoUploaded
        }
    ];

    // Функции проверки доступности перехода к следующему шагу
    const canGoToNextStep = useCallback((stepIndex) => {
        // В режиме редактирования всегда можно переходить дальше
        if (isEditMode) return true;

        // В режиме создания нового отчета проверяем заполненность текущего шага
        switch (stepIndex) {
            case 0: // Время работ → Работы
                return timeStart.status && timeEnd.status;
            case 1: // Работы → Фотографии
                return workSpecified;
            case 2: // Фотографии → Отправка
                return photoUploaded;
            default:
                return false;
        }
    }, [isEditMode, timeStart.status, timeEnd.status, workSpecified, photoUploaded]);

    // Функция проверки возможности отправки отчета
    const canSubmit = useCallback(() => {
        // В режиме редактирования можно отправлять всегда
        if (isEditMode) return true;

        // В режиме создания требуется заполнение всех шагов
        return timeStart.status && timeEnd.status && workSpecified && photoUploaded;
    }, [isEditMode, timeStart.status, timeEnd.status, workSpecified, photoUploaded]);

    const handleSetData = useCallback(() => {
        if (!timeStart.time || !timeEnd.time) {
            message.warning('Укажите время начала и окончания работ');
            setCurrentStep(0);
            return;
        }

        const t_start = timeStart.time.unix();
        const t_end = timeEnd.time.unix();

        const worksIdsArray = [];
        const worksCommentsArray = [];

        check.forEach(item => {
            if (item.checked) {
                worksIdsArray.push(item.id);
                worksCommentsArray.push(item.inputVal || '');
            }
        });

        const reportData = {
            plan_id: plan.id,
            object_id: object.id,
            time_start: t_start,
            time_end: t_end,
            time_download: Math.floor(Date.now() / 1000),
            time_plan: plan.used_date,
            works_id: worksIdsArray.join(','),
            comments_id: worksCommentsArray.join('`'),
            updateFlag: isEditMode,
            report_id: report?.id
        };

        onSubmit(reportData);
        onClose();
    }, [timeStart, timeEnd, check, plan, object.id, isEditMode, report, onSubmit, onClose]);

    const nextObject = useCallback(() => {
        if (!nextReason.text.trim()) {
            message.warning('Укажите причину пропуска');
            return;
        }

        const reportData = {
            plan_id: plan.id,
            object_id: object.id,
            time_start: 0,
            time_end: 0,
            time_download: Math.floor(Date.now() / 1000),
            time_plan: plan.used_date,
            works_id: '0',
            comments_id: nextReason.text,
            updateFlag: false,
            skip: true
        };

        onSubmit(reportData);
        onClose();
    }, [nextReason.text, plan, object.id, onSubmit, onClose]);

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className={s.stepContent}>
                        <Time
                            setTimeStart={setTimeStart}
                            setTimeEnd={setTimeEnd}
                            timeStart={timeStart}
                            timeEnd={timeEnd}
                        />
                    </div>
                );
            case 1:
                return (
                    <div className={s.stepContent}>
                        <Report
                            check={check}
                            setCheck={setCheck}
                            setwork={() => setWorkSpecified(true)}
                            reportsTemp={reportsTemp}
                        />
                    </div>
                );
            case 2:
                return (
                    <NewUpload
                        object={object}
                        setCurrentStep={setCurrentStep}
                        steps={steps}
                        photoUploaded={photoUploaded}
                        setPhotoUploaded={setPhotoUploaded}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className={s.container}>
            {/* Header */}
            <div className={s.headerContent}>
                <Title level={4} className={s.objectName}>
                    {isEditMode ? '✏️ Редактирование отчета' : '📝 Новый отчет'}: {' '}
                    <span style={{color: '#1676fd', fontWeight: 'bold'}}>{object.name}</span>
                </Title>
                {object.long_name && (
                    <Text type="secondary" className={s.objectDescription}>
                        {object.long_name}
                    </Text>
                )}
                {isEditMode && (
                    <Text type="warning" style={{display: 'block', marginTop: 4}}>
                        ✓ Режим редактирования: можно свободно переходить между шагами
                    </Text>
                )}
            </div>

            {/* Progress Steps */}
            <div className={s.stepsContainer}>
                <Steps current={currentStep} className={s.steps} responsive={false}>
                    {steps.map((step, index) => (
                        <Step
                            key={index}
                            title={step.title}
                            icon={step.icon}
                            status={step.completed ? "finish" : "wait"}
                        />
                    ))}
                </Steps>
            </div>

            {/* Main Content */}
            <div className={s.content}>
                {renderStepContent()}
            </div>

            {/* Navigation */}
            <div className={s.navigation}>
                {/* Кнопка "Назад" */}
                {currentStep > 0 && (
                    <Button
                        onClick={() => setCurrentStep(prev => prev - 1)}
                        className={s.navBtn}
                    >
                        <LeftOutlined /> Назад
                    </Button>
                )}

                {/* Кнопка "Отменить" на первом шаге */}
                {currentStep === 0 && (
                    <Button
                        onClick={onClose}
                        className={s.navBtn}
                    >
                        Отменить
                    </Button>
                )}

                {/* Кнопка "Далее" */}
                {currentStep < steps.length - 1 && (
                    <Button
                        type="primary"
                        onClick={() => setCurrentStep(prev => prev + 1)}
                        disabled={!canGoToNextStep(currentStep)}
                        className={s.navBtn}
                    >
                        Далее
                        {!isEditMode && !canGoToNextStep(currentStep) && (
                            <span style={{fontSize: '12px', display: 'block'}}>
                                {currentStep === 0 ? 'Заполните время' :
                                    currentStep === 1 ? 'Выберите работы' : 'Загрузите фото'}
                            </span>
                        )}
                    </Button>
                )}

                {/* Кнопка "Отправить отчет" */}
                {currentStep === steps.length - 1 && (
                    <Button
                        type="primary"
                        onClick={handleSetData}
                        icon={<MailOutlined/>}
                        size="large"
                        disabled={!canSubmit()}
                        className={s.submitBtn}
                    >
                        {isEditMode ? '💾 Сохранить изменения' : '📤 Отправить отчет'}
                        {!isEditMode && !canSubmit() && (
                            <span style={{fontSize: '12px', display: 'block'}}>
                                Заполните все шаги
                            </span>
                        )}
                    </Button>
                )}
            </div>

            {/* Skip Section - только для нового отчета */}
            {!isEditMode && (
                <>
                    <Divider className={s.divider}>или</Divider>

                    <Card className={s.skipCard}>
                        <div className={s.skipHeader}>
                            <CloseCircleOutlined className={s.skipIcon}/>
                            <Text strong>Пропустить объект</Text>
                        </div>
                        <TextArea
                            placeholder="Укажите причину пропуска объекта..."
                            value={nextReason.text}
                            onChange={(e) => setNextReason(prev => ({
                                ...prev,
                                text: e.target.value,
                                status: e.target.value.trim().length > 0
                            }))}
                            rows={2}
                            className={s.skipInput}
                        />
                        <Button
                            onClick={nextObject}
                            type="default"
                            disabled={!nextReason.text.trim()}
                            block
                            className={s.skipBtn}
                        >
                            Пропустить объект
                        </Button>
                    </Card>
                </>
            )}
        </div>
    );
};

export default MobileReport;